import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSupabase } from "@/hooks/useSupabase";
import { useToast } from "@/hooks/use-toast";

const featuredArticles = [
  {
    id: 1,
    title: "Understanding Large Language Models",
    description: "A deep dive into how LLMs work and their capabilities.",
    category: "AI Fundamentals",
    premium: false,
    readTime: "8 min read",
    date: "Apr 12, 2025"
  },
  {
    id: 2,
    title: "The Future of Generative AI in Enterprise",
    description: "How businesses are leveraging AI for competitive advantage.",
    category: "Business",
    premium: true,
    readTime: "12 min read",
    date: "Apr 10, 2025"
  },
  {
    id: 3,
    title: "AI Ethics: Navigating the Challenges",
    description: "Exploring ethical considerations in artificial intelligence development.",
    category: "Ethics",
    premium: false,
    readTime: "10 min read",
    date: "Apr 8, 2025"
  }
];

const FeaturedArticlesSection = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useSupabase();
  const { toast } = useToast();
  
  const handleReadArticle = (id: number, isPremium: boolean) => {
    if (isPremium && !isLoggedIn) {
      toast({
        title: "Premium Content",
        description: "Please log in to read premium articles",
        variant: "destructive",
      });
      navigate('/login', { state: { returnTo: `/article/${id}` } });
      return;
    }
    navigate(`/article/${id}`);
  };
  
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Featured Articles</h2>
          <div 
            className="text-ai-indigo hover:text-ai-purple flex items-center gap-1 cursor-pointer"
            onClick={() => navigate('/articles')}
          >
            View all <ArrowRight className="h-4 w-4" />
          </div>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {featuredArticles.map((article) => (
            <div
              key={article.id}
              className="focus:outline-none focus:ring-4 focus:ring-ai-indigo/30 rounded-lg group cursor-pointer"
              tabIndex={0}
              aria-label={`Read article: ${article.title}`}
              onClick={() => handleReadArticle(article.id as number, article.premium)}
            >
              <Card className="overflow-hidden hover:shadow-md transition-shadow duration-300 group-hover:shadow-lg group-hover:scale-[1.02] group-hover:border-ai-indigo flex flex-col h-full">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <Badge variant="outline" className="text-xs font-medium text-gray-500 border-gray-200">
                      {article.category}
                    </Badge>
                    {article.premium && (
                      <Badge className="bg-premium text-premium-foreground">
                        <Sparkles className="h-3 w-3 mr-1" /> Premium
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl mt-2 group-hover:text-ai-indigo transition-colors">
                    {article.title}
                  </CardTitle>
                  <CardDescription className="text-gray-500 text-sm">
                    {article.readTime} • {article.date}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{article.description}</p>
                </CardContent>
                <CardFooter>
                  <Button
                    variant="ghost"
                    className="text-ai-indigo hover:text-ai-purple hover:bg-ai-indigo/10"
                    tabIndex={-1}
                  >
                    <span>
                      Read Article <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedArticlesSection;
